/**
 * Admin Dashboard RPC definitions (CLA-427)
 *
 * The aggregate RpcGroup powering the global Dashboard cockpit. Two read-only
 * endpoints, both admin-gated:
 *
 *  - `summary` — the headline counters: the acquisition pipeline (one count per
 *    intake stage plus the leads funnel-head), the fleet health rollup
 *    (active / provisioning / error / heartbeat-unhealthy) and the revenue
 *    rollup (MRR cents, new-this-month cents, active-sub count, past-due count).
 *  - `attention` — a typed "needs a human" feed merging stuck intakes, agents in
 *    error or with a stale heartbeat, past-due subscriptions and
 *    expiring/unclicked magic links. Every item carries a typed
 *    {@link AttentionActionRef} (`kind` + `targetId`) so the UI can deep-link to
 *    the offending record.
 *
 * All thresholds that decide what is "stuck" / "expiring" / "unhealthy" are the
 * documented constants in {@link DashboardService}.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, Unauthorized, ValkeyError } from "../errors.ts";

// ============================================================================
// Summary response schemas
// ============================================================================

/**
 * Acquisition pipeline counts — the leads funnel-head plus one count per intake
 * lifecycle stage. The per-stage keys mirror {@link IntakeStatus} exactly.
 *
 * @since 1.0.0
 */
export const PipelineCounts = Schema.Struct({
  leads: Schema.Number,
  draft: Schema.Number,
  sent: Schema.Number,
  signed_up: Schema.Number,
  subscribed: Schema.Number,
  provisioning: Schema.Number,
  completed: Schema.Number,
  abandoned: Schema.Number,
});
export type PipelineCounts = Schema.Schema.Type<typeof PipelineCounts>;

/**
 * Fleet health rollup. `active` / `provisioning` / `error` are agent lifecycle
 * statuses; `unhealthy` is the count of agents that are lifecycle-`active` yet
 * whose Valkey heartbeat is stale (node health derived as `offline`).
 *
 * @since 1.0.0
 */
export const FleetCounts = Schema.Struct({
  active: Schema.Number,
  provisioning: Schema.Number,
  error: Schema.Number,
  unhealthy: Schema.Number,
});
export type FleetCounts = Schema.Schema.Type<typeof FleetCounts>;

/**
 * Revenue rollup, all monetary values in integer cents.
 *
 *  - `mrrCents` — monthly recurring revenue: every active subscription's amount
 *    normalized to a monthly cadence (weekly → ×52/12, monthly → ×1,
 *    annual → ÷12) and summed.
 *  - `newThisMonthCents` — the MRR contribution of active subscriptions created
 *    in the current calendar (UTC) month.
 *  - `activeSubCount` — number of `active` subscriptions.
 *  - `pastDueCount` — number of `past_due` subscriptions.
 *
 * @since 1.0.0
 */
export const RevenueSummary = Schema.Struct({
  mrrCents: Schema.Number,
  newThisMonthCents: Schema.Number,
  activeSubCount: Schema.Number,
  pastDueCount: Schema.Number,
});
export type RevenueSummary = Schema.Schema.Type<typeof RevenueSummary>;

/**
 * Discord bot-pool inventory rollup (CLA-720). `available` is the count of
 * unallocated pool bots — the operational runway before a customer connect
 * strands on an empty pool; `assigned` is the count consumed by installs. The
 * pool is stocked manually and consumed permanently, so a shrinking
 * `available` is the signal to restock.
 *
 * @since 1.0.0
 */
export const DiscordPoolSummary = Schema.Struct({
  available: Schema.Number,
  assigned: Schema.Number,
});
export type DiscordPoolSummary = Schema.Schema.Type<typeof DiscordPoolSummary>;

/**
 * The full `summary` payload — the rollups the cockpit header renders.
 *
 * @since 1.0.0
 */
export const DashboardSummary = Schema.Struct({
  pipeline: PipelineCounts,
  fleet: FleetCounts,
  revenue: RevenueSummary,
  discordPool: DiscordPoolSummary,
});
export type DashboardSummary = Schema.Schema.Type<typeof DashboardSummary>;

// ============================================================================
// Attention feed schemas
// ============================================================================

/**
 * The kind of record an {@link AttentionItem} points at. Drives the UI deep-link
 * target.
 *
 * @since 1.0.0
 */
export const AttentionActionKind = Schema.Literal("intake", "agent", "subscription");
export type AttentionActionKind = Schema.Schema.Type<typeof AttentionActionKind>;

/**
 * Discriminator for an attention feed item:
 *
 *  - `stuck_intake` — intake parked in a non-terminal stage past the stuck-days
 *    threshold.
 *  - `agent_error` — agent in lifecycle status `error`.
 *  - `agent_unhealthy` — lifecycle-`active` agent with a stale heartbeat.
 *  - `past_due_subscription` — subscription in status `past_due`.
 *  - `expiring_link` — intake whose magic link is unclicked and within the
 *    expiry-warning window (or already expired).
 *
 * @since 1.0.0
 */
export const AttentionType = Schema.Literal(
  "stuck_intake",
  "agent_error",
  "agent_unhealthy",
  "past_due_subscription",
  "expiring_link",
);
export type AttentionType = Schema.Schema.Type<typeof AttentionType>;

/**
 * Typed deep-link reference carried by every {@link AttentionItem}.
 *
 * @since 1.0.0
 */
export const AttentionActionRef = Schema.Struct({
  kind: AttentionActionKind,
  targetId: Schema.String,
});
export type AttentionActionRef = Schema.Schema.Type<typeof AttentionActionRef>;

/**
 * A single entry in the attention feed: a typed reason plus a human title /
 * detail plus the deep-link {@link AttentionActionRef}.
 *
 * @since 1.0.0
 */
export const AttentionItem = Schema.Struct({
  type: AttentionType,
  title: Schema.String,
  detail: Schema.String,
  actionRef: AttentionActionRef,
});
export type AttentionItem = Schema.Schema.Type<typeof AttentionItem>;

/**
 * The `attention` payload — the merged feed across all reason types.
 *
 * @since 1.0.0
 */
export const AttentionFeed = Schema.Array(AttentionItem);
export type AttentionFeed = Schema.Schema.Type<typeof AttentionFeed>;

// ============================================================================
// Request schemas
// ============================================================================

/**
 * `summary` takes no arguments — the rollups are global.
 *
 * @since 1.0.0
 */
export const DashboardSummaryInput = Schema.Struct({});
export type DashboardSummaryInput = Schema.Schema.Type<typeof DashboardSummaryInput>;

/**
 * `attention` takes no arguments — the feed is global.
 *
 * @since 1.0.0
 */
export const DashboardAttentionInput = Schema.Struct({});
export type DashboardAttentionInput = Schema.Schema.Type<typeof DashboardAttentionInput>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Dashboard RPC group.
 *
 * Both endpoints require admin role via `AdminMiddleware`. Tags are namespaced
 * under `admin/dashboard/`.
 *
 * @since 1.0.0
 */
export class AdminDashboardRpcs extends RpcGroup.make(
  Rpc.make("summary", {
    payload: DashboardSummaryInput,
    success: DashboardSummary,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError, ValkeyError),
  }).middleware(AdminMiddleware),
  Rpc.make("attention", {
    payload: DashboardAttentionInput,
    success: AttentionFeed,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError, ValkeyError),
  }).middleware(AdminMiddleware),
).prefix("admin/dashboard/") {}
